import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import InstagramProfileHeader from './InstagramProfileHeader';
import InstagramPostGrid from './InstagramPostGrid';
import InstagramPostModal from './InstagramPostModal';
import InstagramMobilePostView from './InstagramMobilePostView';

function InstagramFeed() {
  const [posts, setPosts] = useState([]);
  const [selectedPost, setSelectedPost] = useState(null);
  const [error, setError] = useState(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  const CODA_API_TOKEN = process.env.REACT_APP_CODA_API_TOKEN || '';
  const DOC_ID = process.env.REACT_APP_CODA_DOC_ID || 'AC5b6N6F3q';
  const TABLE_ID = process.env.REACT_APP_CODA_TABLE_ID || 'grid-0bhi2tOMaD';

  useEffect(() => {
    document.title = 'The Dorcol Residence Instagram Preview';
  }, []);

  useEffect(() => {
    const handlePlay = (e) => {
      const allVideos = document.querySelectorAll('video');
      allVideos.forEach((video) => {
        if (video !== e.target) {
          video.pause();
        }
      });
    };

    const videos = document.querySelectorAll('video');
    videos.forEach((video) => {
      video.addEventListener('play', handlePlay);
    });

    return () => {
      videos.forEach((video) => {
        video.removeEventListener('play', handlePlay);
      });
    };
  }, [posts]);

  useEffect(() => {
    if (selectedPost) {
      document.body.style.overflow = 'hidden';
      document.body.style.paddingRight = `${window.innerWidth - document.documentElement.clientWidth}px`;
    } else {
      document.body.style.overflow = '';
      document.body.style.paddingRight = '';
    }

    return () => {
      document.body.style.overflow = '';
      document.body.style.paddingRight = '';
    };
  }, [selectedPost]);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handlePostSelect = (post) => {
    setSelectedPost(post);
    if (isMobile) {
      window.history.pushState({ postId: post.id }, '', `#post-${post.id}`);
    }
  };

  useEffect(() => {
    const handlePopState = () => {
      setSelectedPost(null);
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  const getImageUrl = (values) => {
    const images = values?.Image;
    if (Array.isArray(images) && images.length > 0) {
      return images.map(image => image.url || null).filter(Boolean);
    }
    return [];
  };

  const getMediaUrl = useCallback((values) => {
    const imageUrls = getImageUrl(values);
    if (imageUrls.length > 0) {
      return { urls: imageUrls, isVideo: false, isMultiple: imageUrls.length > 1 };
    }

    const files = values['c-cVRXouqMC4'] || '';
    if (files.trim()) {
      const fileNames = files.split(',').map(f => f.trim()).filter(Boolean);
      if (fileNames.length > 0) {
        const urls = fileNames.map(fileName => 
          `https://res.cloudinary.com/dexruw0ki/image/upload/SuzanneCode/May2025/${fileName}`
        );
        return { urls, isVideo: false, isMultiple: urls.length > 1 };
      }
    }

    const richVideo = values['Video Link'];
    if (richVideo && typeof richVideo === 'object' && richVideo.url) {
      return { urls: [richVideo.url], isVideo: true, isMultiple: false };
    }

    return { urls: [], isVideo: false, isMultiple: false };
  }, []);

  const getMediaSources = useCallback((values) => {
    // Get image sources
    const imageUrls = getImageUrl(values);
    let primaryImageUrl = null;
    
    if (imageUrls.length > 0) {
      primaryImageUrl = imageUrls[0];
    } else {
      // Fallback to Cloudinary files
      const files = values['c-cVRXouqMC4'] || '';
      if (files.trim()) {
        const fileNames = files.split(',').map(f => f.trim()).filter(Boolean);
        if (fileNames.length > 0) {
          primaryImageUrl = `https://res.cloudinary.com/dexruw0ki/image/upload/SuzanneCode/May2025/${fileNames[0]}`;
        }
      }
    }

    // Get video source
    const richVideo = values['Video Link'];
    const primaryVideoUrl = (richVideo && typeof richVideo === 'object' && richVideo.url) 
      ? richVideo.url 
      : null;

    return {
      primaryImageUrl,
      primaryVideoUrl,
      hasImage: !!primaryImageUrl,
      hasVideo: !!primaryVideoUrl
    };
  }, []);

  useEffect(() => {
    async function fetchPosts() {
      try {
        const res = await axios.get(
          `https://coda.io/apis/v1/docs/${DOC_ID}/tables/${TABLE_ID}/rows?useColumnNames=true&valueFormat=rich`,
          {
            headers: {
              Authorization: `Bearer ${CODA_API_TOKEN}`,
            },
          }
        );

        if (!res.data || !Array.isArray(res.data.items)) {
          throw new Error('Unexpected API response structure: "items" field missing.');
        }

        const formattedPosts = res.data.items.map((row) => {
          const values = row.values || {};
          const media = getMediaUrl(values);
          const sources = getMediaSources(values);

          // Determine thumbnail and video URLs based on what's available
          let thumbnailUrl = null;
          let videoUrl = null;

          if (sources.hasImage && sources.hasVideo) {
            // Both exist: thumbnail = image, video = video
            thumbnailUrl = sources.primaryImageUrl;
            videoUrl = sources.primaryVideoUrl;
          } else if (sources.hasImage) {
            // Image only: thumbnail = image, no video
            thumbnailUrl = sources.primaryImageUrl;
            videoUrl = null;
          } else if (sources.hasVideo) {
            // Video only: thumbnail = video (as poster), video = video
            thumbnailUrl = sources.primaryVideoUrl;
            videoUrl = sources.primaryVideoUrl;
          }

          return {
            id: row.id,
            name: values['Title']?.replace(/```/g, '').replace(/`/g, '').trim() || 'Untitled Post',
            date: values['Planned publish date']
              ? new Date(values['Planned publish date']).toLocaleDateString('en-GB', {
                  day: 'numeric',
                  month: 'short',
                  year: 'numeric',
                })
              : 'Date TBC',
            rawDate: values['Planned publish date']
              ? new Date(values['Planned publish date'])
              : new Date(0),
            caption: values['Caption']?.replace(/```/g, '').replace(/`/g, '').trim() || '',
            mediaUrls: media.urls,
            mediaUrl: media.urls.length > 0 ? media.urls[0] : null, // Keep for backward compatibility
            isVideo: media.isVideo,
            postType: values['Type']?.replace(/```/g, '').replace(/`/g, '').trim() || 'Post',
            approved: values['Approved?'] || false,
            isCarousel: (values['Type']?.replace(/```/g, '').replace(/`/g, '').trim() || 'Post') === 'Carousel' || (media.isMultiple && !media.isVideo),
            hasMultipleImages: media.isMultiple && !media.isVideo,
            hashtags: values['Hashtags']?.replace(/```/g, '').replace(/`/g, '').trim() || '',
            browserLink: row.browserLink,
            // New dual-media fields
            thumbnailUrl,
            videoUrl,
            hasImage: sources.hasImage,
            hasVideo: sources.hasVideo,
          };
        }).sort((a, b) => b.rawDate - a.rawDate);

        setPosts(formattedPosts);
      } catch (err) {
        setError('Could not load Instagram posts.');
      }
    }

    fetchPosts();
  }, [getMediaUrl, getMediaSources, CODA_API_TOKEN, DOC_ID, TABLE_ID]);

  useEffect(() => {
    if ('IntersectionObserver' in window) {
      const videoObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.target.tagName === 'VIDEO') {
              if (entry.isIntersecting) {
                if (entry.target.paused) {
                  const playPromise = entry.target.play();
                  if (playPromise !== undefined) {
                    playPromise.catch(() => {
                      entry.target.muted = true;
                    });
                  }
                }
              } else {
                if (!entry.target.paused) {
                  entry.target.pause();
                }
              }
            }
          });
        },
        {
          root: null, // Use the viewport
          rootMargin: '0px',
          threshold: 0.5, // 50% of the video must be visible
        }
      );

      const videos = document.querySelectorAll('video');
      videos.forEach((video) => {
        videoObserver.observe(video);
      });

      return () => {
        videos.forEach((video) => {
          videoObserver.unobserve(video);
        });
      };
    }
  }, [posts]);

  return (
    <div className="p-0 sm:p-4">
      <div className="w-full sm:max-w-7xl sm:mx-auto">
        <InstagramProfileHeader postCount={posts.length} />

        {error && <div className="text-red-600 text-center mb-4">{error}</div>}
        
        <InstagramPostGrid posts={posts} onPostSelect={handlePostSelect} />

        {/* Conditional rendering based on device */}
        {isMobile ? (
          <InstagramMobilePostView 
            post={selectedPost} 
            onClose={() => setSelectedPost(null)} 
          />
        ) : (
          selectedPost && (
            <InstagramPostModal 
              post={selectedPost} 
              onClose={() => setSelectedPost(null)} 
            />
          )
        )}
      </div>
    </div>
  );
}

export default InstagramFeed;
